
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Dumbbell, Clock } from "lucide-react"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"

interface UserExercisePlansProps {
  userId: string
  exercisePlans: any[]
}

export function UserExercisePlans({ userId, exercisePlans = [] }: UserExercisePlansProps) {
  const { t } = useTranslation()
  const [activePlanIndex, setActivePlanIndex] = useState(0)

  if (!exercisePlans || exercisePlans.length === 0) {
    return <div className="text-center py-8">{t("users.noExercisePlans")}</div>
  }

  const currentPlan = exercisePlans[activePlanIndex]
  const targetMuscles = Array.isArray(currentPlan.targetMuscles) ? currentPlan.targetMuscles : []
  const equipment = Array.isArray(currentPlan.equipment) ? currentPlan.equipment : []
  const exercises = Array.isArray(currentPlan.exercises) ? currentPlan.exercises : []

  return (
    <div className="space-y-6">
      {exercisePlans.length > 1 && (
        <Tabs
          value={activePlanIndex.toString()}
          onValueChange={(value) => setActivePlanIndex(Number.parseInt(value))}
          className="w-full"
        >
          <TabsList className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 w-full">
            {exercisePlans.map((plan, index) => (
              <TabsTrigger key={plan._id} value={index.toString()}>
                {plan.name}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}

      {/* Current plan details */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle>{currentPlan.name}</CardTitle>
          <CardDescription>
            {t("userView.exercise.difficulty")}: {currentPlan.difficulty}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2 text-sm">
            <div className="flex items-center bg-muted px-2 py-1 rounded-md">
              <Dumbbell className="h-4 w-4 mr-1" />
              <span>{currentPlan.type}</span>
            </div>
            <div className="flex items-center bg-muted px-2 py-1 rounded-md">
              <Clock className="h-4 w-4 mr-1" />
              <span>
                {currentPlan.duration} {t("userView.exercise.minutes")}
              </span>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium mb-2">{t("userView.exercise.description")}</h3>
            <p className="text-sm">{currentPlan.description}</p>
          </div>

          <div>
            <h3 className="text-sm font-medium mb-2">{t("userView.exercise.targetMuscles")}</h3>
            <div className="flex flex-wrap gap-2">
              {targetMuscles.length > 0 ? (
                targetMuscles.map((muscle: string, index: number) => (
                  <Badge key={index} variant="secondary">
                    {muscle}
                  </Badge>
                ))
              ) : (
                <span className="text-muted-foreground">{t("userView.exercise.noTargetMuscles")}</span>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium mb-2">{t("userView.exercise.equipment")}</h3>
            <div className="flex flex-wrap gap-2">
              {equipment.length > 0 ? (
                equipment.map((item: string, index: number) => (
                  <Badge key={index} variant="outline">
                    {item}
                  </Badge>
                ))
              ) : (
                <span className="text-muted-foreground">{t("userView.exercise.noEquipment")}</span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Exercise list */}
      <Card>
        <CardHeader>
          <CardTitle>{t("userView.exercise.exercises")}</CardTitle>
        </CardHeader>
        <CardContent>
          {exercises.length > 0 ? (
            <Accordion type="single" collapsible className="w-full">
              {exercises.map((exercise: any, index: number) => (
                <AccordionItem key={index} value={`exercise-${index}`}>
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center justify-between w-full pr-4">
                      <span>{exercise.name}</span>
                      <span className="text-sm text-muted-foreground">
                        {exercise.sets} × {exercise.reps}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2">
                      <p className="text-sm">{exercise.description}</p>
                      <p className="text-sm text-muted-foreground">
                        {t("userView.exercise.rest")}: {exercise.rest}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <div className="text-center py-4">
              <p className="text-muted-foreground">{t("userView.exercise.noExercises")}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Plan metadata */}
      <div className="text-sm text-muted-foreground">
        <p>
          {t("userView.exercise.assignedOn")}: {new Date(currentPlan.createdAt).toLocaleDateString()}
        </p>
        <p>
          {t("userView.exercise.lastUpdated")}: {new Date(currentPlan.updatedAt).toLocaleDateString()}
        </p>
      </div>
    </div>
  )
}
